import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import NotFound from "@/views/NotFound.vue";
import { getUser } from "@/api";
import validation from "@/validation";
import { done, toast } from "@/tools";
import store from "@/store";
import {getCookie, removeCookie, setCookie} from 'typescript-cookie'
import { hasAdvancedAccess } from '@/ownership';


const routes: Array<RouteRecordRaw> = [
    { path: '/', name: 'home', component: () => import('@/views/HomePage.vue') },
    { path: '/login', name: 'login', component: () => import('@/views/Login.vue') },
    { path: '/register', name: 'register', component: () => import('@/views/Register.vue') },
    { path: '/claim', name: 'claim.account', component: () => import('@/views/ClaimAccount.vue') },
    { path: '/terms', name: 'terms.public', component: () => import('@/views/PublicTerms.vue') },
    { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/ForgotPassword.vue') },
    { path: '/migrate', name: 'migrate', component: () => import('@/views/Migrate.vue'), meta: { auth: true }, },
    { path: '/reset-password/:token', name: 'reset-password', component: () => import('@/views/ResetPassword.vue') },
    { path: '/banned', name: 'banned', component: () => import('@/views/Banned.vue') },
    { path: '/player-list/:token', name: 'player-list.public', component: () => import('@/views/PlayerListPublic.vue') },
    { path: '/docs/:edition?/:module?', name: 'docs', component: () => import(/* webpackChunkName: "group-docs" */ '@/views/Documentation.vue') },
    { path: '/media', name: 'media', component: () => import(/* webpackChunkName: "group-media" */ '@/views/Media.vue') },

    { path: '/gift/:token', name: 'gift.redeem', component: () => import('@/views/GiftRedeem.vue') },
    { path: '/r/:slug', name: 'reseller.storefront', component: () => import('@/views/ResellerStorefront.vue') },
    {
        name: 'referral.code',
        path: '/referral/:code',
        component: () => {},
        beforeEnter(to, from, next) {
            const code = Array.isArray(to.params.code) ? to.params.code[0] : to.params.code;
            if (!code || code.length < 4 || code.length > 99) {
                toast('error', 'That is not a valid referral code');
                return next({ name: 'dashboard.home' });
            }
            // @ts-ignore
            setCookie('referral' , code);
            toast('info', 'Referral Code Applied')
            return next({ name: 'dashboard.purchase' });
        }
    },
    {
        path: '/email/verify/:id/:hash',
        name: 'verify-email',
        component: () => import('@/views/VerifyEmail.vue'),
        meta: { skipMigrate: true },
    }, {
        path: '/popout',
        name: 'modules-popout',
        component: () => import('@/views/ModulesPopout.vue'),
        meta: { auth: true, owned: true },
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        redirect: '/dashboard/home',
        component: () => import('@/views/Dashboard.vue'),
        meta: { auth: true },
        children: [
            { name: 'dashboard.home', path: 'home', component: () => import('@/views/dashboard/Home.vue') },
            {
                name: 'dashboard.modules',
                path: 'modules',
                component: () => import('@/views/dashboard/Modules.vue'),
                meta: { owned: true }
            },
            { name: 'dashboard.settings', path: 'settings', component: () => import('@/views/dashboard/Settings.vue') },
            { name: 'dashboard.terms', path: 'terms', component: () => import('@/views/dashboard/Terms.vue') },
            {
                name: 'dashboard.tickets',
                path: 'tickets',
                component: () => import(/* webpackChunkName: "group-tickets" */ '@/views/dashboard/Tickets.vue')
            },
            {
                name: 'dashboard.tickets.create',
                path: 'tickets/create',
                component: () => import(/* webpackChunkName: "group-tickets" */ '@/views/dashboard/tickets/CreateTicket.vue')
            },
            {
                name: 'dashboard.tickets.view',
                path: 'tickets/:ticket',
                component: () => import(/* webpackChunkName: "group-tickets" */ '@/views/dashboard/tickets/ViewTicket.vue'),
                beforeEnter(to, from, next) {
                    if (validation.isInteger(to.params.ticket as string)) return next();
                    toast('error', 'That is not a valid ticket');
                    return next({ name: 'dashboard.tickets' });
                }
            },
            {
                name: 'dashboard.purchase',
                path: 'purchase',
                component: () => import('@/views/dashboard/Purchase.vue'),
                beforeEnter(to, from) {
                    if (getCookie('referral') != null){
                        store.state.referral = getCookie('referral') as string;
                        removeCookie('referral');
                    }
                }
            },
            {
                name: 'dashboard.reseller',
                path: 'reseller',
                component: () => import('@/views/dashboard/Reseller.vue'),
                meta: { reseller: true }
            },
            {
                name: 'dashboard.download',
                path: 'download',
                component: () => import('@/views/dashboard/Download.vue'),
                meta: { ownedAny: true }
            },
            {
                name: 'dashboard.configs',
                path: 'configs',
                component: () => import('@/views/dashboard/configs/Configs.vue'),
                meta: { ownedAny: true }
            },
            {
                name: 'dashboard.scripts',
                path: 'scripts',
                component: () => import('@/views/dashboard/scripts/Scripts.vue'),
                meta: { ownedAdvanced: true }
            },
            {
                name: 'dashboard.guides',
                path: 'guides',
                component: () => import('@/views/dashboard/guide/Guides.vue'),
                meta: { ownedAny: true }
            },
            {
                name: 'dashboard.referrals',
                path: 'referral',
                component: () => import('@/views/dashboard/Referrals.vue'),
                meta: { minAccess: 1, maxAccess: 1 }
            },
            {
                name: 'dashboard.guides.create',
                path: 'guides/create',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/guide/CreateGuide.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.guides.edit',
                path: 'guides/:guide/edit',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/guide/CreateGuide.vue'),
                meta: { minAccess: 3 },
                beforeEnter(to, from, next) {
                    if (validation.isInteger(to.params.guide as string)) return next();
                    toast('error', 'That is not a valid guide');
                    return next({ name: 'dashboard.guides' });
                }
            },
            {
                name: 'dashboard.guides.view',
                path: 'guides/:guide',
                component: () => import('@/views/dashboard/guide/ViewGuide.vue'),
                meta: { ownedAny: true },
                beforeEnter(to, from, next) {
                    if (validation.isInteger(to.params.guide as string)) return next();
                    toast('error', 'That is not a valid guide');
                    return next({ name: 'dashboard.guides' });
                }
            },
            {
                name: 'dashboard.configs.publish',
                path: 'configs/publish',
                component: () => import('@/views/dashboard/configs/PublishConfig.vue'),
                meta: { owned: true }
            },
            {
                name: 'dashboard.changelog.create',
                path: 'changelog/create',
                component: () => import('@/views/dashboard/changelog/CreateChangelog.vue'),
                meta: { minAccess: 4 }
            },
            {
                name: 'dashboard.manage',
                path: 'manage',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/Manage.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.manage.users',
                path: 'manage/users',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageUsers.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.manage.users.view',
                path: 'manage/users/:user',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ViewUser.vue'),
                meta: { minAccess: 3 },
                beforeEnter(to, from, next) {
                    if ('user' in to.params && validation.isInteger(to.params.user as string)) return next();
                    toast('error', 'That is not a valid user');
                    return next({ name: 'dashboard.manage.users' });
                }
            },
            {
                name: 'dashboard.manage.payments',
                path: 'manage/payments',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManagePayments.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.manage.appeals',
                path: 'manage/appeals',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageBanAppeals.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.manage.appeals.view',
                path: 'manage/appeals/:appeal',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ViewBanAppeal.vue'),
                meta: { minAccess: 3 },
                beforeEnter(to, from, next) {
                    if ('appeal' in to.params && validation.isInteger(to.params.appeal as string)) return next();
                    toast('error', 'That is not a valid appeal');
                    return next({ name: 'dashboard.manage.appeals' });
                }
            },
            {
                name: 'dashboard.manage.media',
                path: 'manage/media',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageMediaApplications.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.manage.media.view',
                path: 'manage/media/:application',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ViewMediaApplication.vue'),
                meta: { minAccess: 3 },
                beforeEnter(to, from, next) {
                    if ('application' in to.params && validation.isInteger(to.params.application as string)) return next();
                    toast('error', 'That is not a valid media application');
                    return next({ name: 'dashboard.manage.media' });
                }
            },
            {
                name: 'dashboard.manage.giveaways',
                path: 'manage/giveaways',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageGiveaways.vue'),
                meta: { minAccess: 3 }
            },
            {
                name: 'dashboard.manage.vars',
                path: 'manage/vars',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageVars.vue'),
                meta: { minAccess: 4 }
            },
            {
                name: 'dashboard.manage.discounts',
                path: 'manage/discounts',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageDiscountCodes.vue'),
                meta: { minAccess: 4 }
            },
            {
                name: 'dashboard.manage.resellers',
                path: 'manage/resellers',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageTrustedResellers.vue'),
                meta: { minAccess: 4 }
            },
            {
                name: 'dashboard.manage.keys',
                path: 'manage/keys',
                component: () => import(/* webpackChunkName: "group-admin" */ '@/views/dashboard/manage/ManageLicenseKeys.vue'),
                meta: { minAccess: 4 }
            }
        ]
    },
    { path: '/:catchAll(.*)*', name: 'Not Found', component: NotFound }
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.name === 'terms.public') return { left: 0, top: 0 };
        if (savedPosition) return savedPosition;
        if (to.hash) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        el: to.hash,
                        behavior: 'smooth',
                        top: 96,
                    });
                }, 50);
            });
        }
        if (to.path !== from.path) return { top: 0 };
        return undefined;
    },
})

router.beforeEach(async (to, from, next) => {
    const user = await getUser();
    if (user.id !== -1 && user.access === -1 && to.name !== 'banned') return next({ name: 'banned' });
    if (!("auth" in to.meta)) return next();
    if (user.id == -1) return next({ name: 'login' });
    if (user.access >= 2 && !user.two_factor_enabled && to.name !== 'dashboard.settings') {
        toast('error', 'Staff accounts must enable two-factor authentication.');
        return next({ name: 'dashboard.settings' });
    }
    if ('minAccess' in to.meta && (to.meta['minAccess'] as number) > user.access) return next({ name: 'dashboard.home' });
    if ('maxAccess' in to.meta && (to.meta['maxAccess'] as number) < user.access) return next({ name: 'dashboard.home' });
    if ('reseller' in to.meta && !user.reseller_enabled) return next({ name: 'dashboard.home' });
    if ('noOwned' in to.meta && user.owned && user.owned_advanced) return next({ name: 'dashboard.home' });
    if ('owned' in to.meta && !user.owned && user.access < 2) return next({ name: 'dashboard.home' });
    if ('ownedAny' in to.meta && (!user.owned && !user.owned_advanced) && user.access < 2) return next({ name: 'dashboard.home' });
    if ('ownedAdvanced' in to.meta && !hasAdvancedAccess(user)) return next({ name: 'dashboard.home' });
    if ('skipMigrate' in to.meta || to.name === 'migrate') return next();
    if (user.email_verified_at === null) return next({ name: 'migrate' });
    return next();
});


const CHUNK_RELOAD_KEY = 'dream:chunk-reload';

router.onError((error, to) => {
    const message = error instanceof Error ? error.message : String(error);
    const isChunkLoadFailure = /ChunkLoadError|Loading chunk .+ failed|Failed to fetch dynamically imported module/i.test(message);

    if (!isChunkLoadFailure) {
        console.error(error);
        return;
    }

    const target = to.fullPath || window.location.pathname + window.location.search + window.location.hash;
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === target) {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        console.error(error);
        return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, target);
    window.location.assign(target);
});

router.afterEach(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    done();
});

export default router
